import React, { useState, useEffect, useRef } from 'react';
import { Upload, X, Plus, Edit, Trash2, User, Save, AlertCircle, CheckCircle, Loader, Image as ImageIcon, Search, Grid, List, Users, ImageOff } from 'lucide-react';
import AdminPanelHeader from '../../components/common/Header/AdminPanelHeader';
import { apiRequest, apiRequestMultipart } from '../../utils/apiHelper';

const TeamImageUpload = () => {
  const API_BASE_URL = `${process.env.REACT_APP_API_BASE_URL}/api/team-members`;

  // State for team members
  const [teamMembers, setTeamMembers] = useState([]);
  const [initialLoading, setInitialLoading] = useState(true);
  const [brokenImages, setBrokenImages] = useState(new Set());
  const [isDragging, setIsDragging] = useState(false);

  // Ref to the form section so we can scroll it into view when the user clicks Add or Edit
  // (the Edit button lives mid-page; without this the form opens above and the user misses it).
  const formRef = useRef(null);

  // State for form management
  const [showForm, setShowForm] = useState(false);
  const [editingMember, setEditingMember] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  // State for search and filters
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('displayOrder');
  const [viewMode, setViewMode] = useState('grid');

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    position: '',
    description: '',
    displayOrder: 1,
    isActive: true
  });

  // State for image handling
  const [selectedImage, setSelectedImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [showImageModal, setShowImageModal] = useState(false);
  const [modalImage, setModalImage] = useState(null);

  // Form validation errors
  const [errors, setErrors] = useState({});

  // Fetch team members from API (using the correct endpoint)
  const fetchTeamMembers = async () => {
    try {
      setInitialLoading(true);
      // Using the admin endpoint to get all team members
      const response = await apiRequest(`${API_BASE_URL}/admin`);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      console.log('Team members data:', data);
      
      // Backend returns array directly
      setTeamMembers(Array.isArray(data) ? data : []);
      console.log('Team members fetched successfully:', data);
    } catch (error) {
      console.error('Error fetching team members:', error);
      showMessage('Failed to load team members. Check if backend is running.', 'error');
      setTeamMembers([]);
    } finally {
      setInitialLoading(false);
    }
  };

  // Initialize data on component mount
  useEffect(() => {
    fetchTeamMembers();
  }, []);

  // Close image modal on Esc.
  useEffect(() => {
    if (!showImageModal) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') setShowImageModal(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [showImageModal]);

  // Scroll the form into view whenever it opens or the user switches editing target.
  // Use rAF so the layout has actually been committed before we scroll.
  useEffect(() => {
    if (!showForm) return;
    const id = window.requestAnimationFrame(() => {
      if (formRef.current) {
        formRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    });
    return () => window.cancelAnimationFrame(id);
  }, [showForm, editingMember]);

  // Show notification message. Success auto-dismisses; errors stay until the user closes them.
  const showMessage = (text, type = 'success') => {
    setMessage({ text, type });
    if (type === 'success') {
      setTimeout(() => setMessage({ text: '', type: '' }), 5000);
    }
  };

  // Validate form data
  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.name || !formData.name.trim()) {
      newErrors.name = 'Name is required';
    }
    
    if (!formData.position || !formData.position.trim()) {
      newErrors.position = 'Position is required';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form input changes
  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  // Single validator + preview generator shared by file picker, drag-drop, and per-row upload.
  const acceptImageFile = (file) => {
    if (!file) return false;
    if (!file.type.startsWith('image/')) {
      showMessage('Please select a valid image file (JPEG, PNG, GIF)', 'error');
      return false;
    }
    if (file.size > 5 * 1024 * 1024) {
      showMessage('File size must be less than 5MB', 'error');
      return false;
    }
    return true;
  };

  const handleImageSelect = (event) => {
    const file = event.target.files[0];
    if (!acceptImageFile(file)) return;
    setSelectedImage(file);
    const reader = new FileReader();
    reader.onload = (e) => setImagePreview(e.target.result);
    reader.readAsDataURL(file);
  };

  // Clear the currently selected (but not yet submitted) image.
  const clearSelectedImage = (e) => {
    if (e) e.stopPropagation();
    setSelectedImage(null);
    setImagePreview(null);
    const input = document.getElementById('imageInput');
    if (input) input.value = '';
  };

  // Drag and drop handlers for the upload zone.
  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!isDragging) setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    const file = e.dataTransfer.files && e.dataTransfer.files[0];
    if (!acceptImageFile(file)) return;
    setSelectedImage(file);
    const reader = new FileReader();
    reader.onload = (ev) => setImagePreview(ev.target.result);
    reader.readAsDataURL(file);
  };

  // Handle form submission (with or without image)
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      showMessage('Please fix the errors below', 'error');
      return;
    }
    
    setSubmitting(true);
    
    try {
      let response;
      
      if (selectedImage) {
        // Create team member with image using FormData
        const formDataToSend = new FormData();
        formDataToSend.append('name', formData.name.trim());
        formDataToSend.append('position', formData.position.trim());
        formDataToSend.append('description', formData.description?.trim() || '');
        formDataToSend.append('displayOrder', formData.displayOrder || 1);
        formDataToSend.append('isActive', formData.isActive);
        formDataToSend.append('image', selectedImage);

        const url = editingMember
          ? `${API_BASE_URL}/${editingMember.id}/with-image`
          : `${API_BASE_URL}/with-image`;

        response = await apiRequestMultipart(url, formDataToSend, {
          method: editingMember ? 'PUT' : 'POST',
        });
      } else {
        // Create/update team member without image using JSON
        const memberData = {
          name: formData.name.trim(),
          position: formData.position.trim(),
          description: formData.description?.trim() || '',
          displayOrder: formData.displayOrder || 1,
          isActive: formData.isActive
        };

        const url = editingMember 
          ? `${API_BASE_URL}/${editingMember.id}`
          : API_BASE_URL;

        response = await apiRequest(url, {
          method: editingMember ? 'PUT' : 'POST',
          body: JSON.stringify(memberData),
        });
      }

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      console.log('Save result:', result);
      
      if (editingMember) {
        showMessage('Team member updated successfully!', 'success');
      } else {
        showMessage('Team member created successfully!', 'success');
      }
      
      setShowForm(false);
      setEditingMember(null);
      resetForm();
      await fetchTeamMembers();
      
    } catch (error) {
      console.error('Submit error:', error);
      showMessage('Failed to save team member: ' + error.message, 'error');
    } finally {
      setSubmitting(false);
    }
  };

  // Delete team member
  const deleteMember = async (id) => {
    if (!window.confirm('Are you sure you want to delete this team member?')) return;
    
    try {
      const response = await apiRequest(`${API_BASE_URL}/${id}`, {
        method: 'DELETE',
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      setTeamMembers(prev => prev.filter(member => member.id !== id));
      showMessage('Team member deleted successfully!', 'success');
    } catch (error) {
      console.error('Delete error:', error);
      showMessage('Failed to delete team member: ' + error.message, 'error');
    }
  };

  // Handle image upload for existing member
  const handleImageUpload = async (memberId, event) => {
    const file = event.target.files[0];
    if (!acceptImageFile(file)) {
      event.target.value = '';
      return;
    }

    setSubmitting(true);
    try {
      const formDataUpload = new FormData();
      formDataUpload.append('image', file);

      // Use the dedicated image upload endpoint with authentication
      const response = await apiRequestMultipart(`${API_BASE_URL}/${memberId}/upload-image`, formDataUpload, {
        method: 'POST',
      });

      if (!response.ok) {
        throw new Error(`Upload failed: ${response.status}`);
      }

      const updatedMember = await response.json();
      setTeamMembers(prev => prev.map(member =>
        member.id === memberId ? updatedMember : member
      ));
      setBrokenImages(prev => {
        const next = new Set(prev);
        next.delete(memberId);
        return next;
      });

      showMessage('Image uploaded successfully!', 'success');
      return true;
    } catch (error) {
      console.error('Upload error:', error);
      showMessage('Failed to upload image: ' + error.message, 'error');
      return false;
    } finally {
      setSubmitting(false);
      event.target.value = '';
    }
  };

  // Reset form
  const resetForm = () => {
    setFormData({
      name: '',
      position: '',
      description: '',
      displayOrder: teamMembers.length + 1,
      isActive: true
    });
    setErrors({});
    setSelectedImage(null);
    setImagePreview(null);
    const input = document.getElementById('imageInput');
    if (input) input.value = '';
  };

  // Handle edit
  const handleEdit = (member) => {
    setEditingMember(member);
    setFormData({
      name: member.name || '',
      position: member.position || '',
      description: member.description || '',
      displayOrder: member.displayOrder || 1,
      isActive: member.isActive !== false
    });
    setErrors({});
    setSelectedImage(null);
    setImagePreview(null);
    setShowForm(true);
  };

  // Handle form cancel
  const handleCancel = () => {
    setShowForm(false);
    setEditingMember(null);
    resetForm();
  };

  // Show image modal
  const showImageInModal = (imageUrl, memberName) => {
    setModalImage({ url: imageUrl, name: memberName });
    setShowImageModal(true);
  };

  // Get image URL for display - Only Supabase URLs supported
  const getImageUrl = (member) => {
    if (!member.imageUrl) return null;
    
    // Only return if it's a full URL (Supabase URL)
    if (member.imageUrl.startsWith('http')) {
      return member.imageUrl;
    }
    
    // No fallback to local storage - return null for non-Supabase URLs
    console.warn('Non-Supabase URL detected, ignoring:', member.imageUrl);
    return null;
  };

  // Filter and sort team members
  const filteredAndSortedMembers = teamMembers
    .filter(member => {
      const matchesSearch = member.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           member.position.toLowerCase().includes(searchTerm.toLowerCase());
      return matchesSearch;
    })
    .sort((a, b) => {
      switch (sortBy) {
        case 'name':
          return a.name.localeCompare(b.name);
        case 'position':
          return a.position.localeCompare(b.position);
        case 'displayOrder':
          return (a.displayOrder || 0) - (b.displayOrder || 0);
        default:
          return 0;
      }
    });

  // Styles matching your existing theme (unchanged)
  const styles = {
    pageContainer: {
      minHeight: '100vh',
      display: 'flex',
      flexDirection: 'column',
      backgroundColor: '#ffffff'
    },
    
    // Header styling matching your theme
    heroSection: {
      background: '#1a2332',
      backgroundImage: `
        radial-gradient(circle at 25% 25%, rgba(255,255,255,0.1) 1px, transparent 1px),
        radial-gradient(circle at 75% 75%, rgba(255,255,255,0.05) 1px, transparent 1px)
      `,
      backgroundSize: '20px 20px, 30px 30px',
      padding: '2rem 0',
      marginBottom: '0',
      position: 'relative',
      overflow: 'hidden',
      borderBottom: '1px solid rgba(255,255,255,0.1)'
    },
    heroContainer: {
      maxWidth: '1400px',
      margin: '0 auto',
      padding: '0 2rem',
      position: 'relative',
      zIndex: 2
    },
    heroTitle: {
      fontSize: '2rem',
      fontWeight: '600',
      color: '#ffffff',
      margin: '0',
      textAlign: 'center',
      textShadow: '0 1px 2px rgba(0,0,0,0.1)'
    },
    
    // Admin Controls
    adminControls: {
      position: 'absolute',
      right: '2rem',
      top: '50%',
      transform: 'translateY(-50%)',
      display: 'flex',
      alignItems: 'center',
      gap: '1rem',
      zIndex: 3
    },
    adminButton: {
      backgroundColor: '#0ea5e9',
      color: 'white',
      border: 'none',
      padding: '0.75rem 1.5rem',
      borderRadius: '8px',
      fontSize: '1rem',
      fontWeight: '500',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem',
      transition: 'all 0.2s ease',
      boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
    },
    
    // Main Section
    mainSection: {
      backgroundColor: '#ffffff',
      padding: '2rem 0',
      flex: 1
    },
    container: {
      maxWidth: '1400px',
      margin: '0 auto',
      padding: '0 2rem'
    },
    
    // Message Notification — top-center so it lands in the user's eyeline regardless of
    // viewport width or scroll position. Stays above AdminPanelHeader's sticky styling.
    message: {
      position: 'fixed',
      top: '1rem',
      left: '50%',
      transform: 'translateX(-50%)',
      zIndex: 1500,
      padding: '0.875rem 1.25rem',
      borderRadius: '8px',
      fontWeight: '500',
      boxShadow: '0 10px 30px rgba(0, 0, 0, 0.18)',
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem',
      minWidth: '320px',
      maxWidth: 'min(640px, calc(100vw - 2rem))',
      animation: 'toastSlideDown 0.28s ease-out'
    },
    successMessage: {
      backgroundColor: '#d1fae5',
      color: '#065f46',
      border: '1px solid #a7f3d0'
    },
    errorMessage: {
      backgroundColor: '#fee2e2',
      color: '#991b1b',
      border: '1px solid #fca5a5'
    },

    // Actions Bar
    actionsBar: {
      backgroundColor: 'white',
      padding: '1.5rem',
      borderRadius: '12px',
      marginBottom: '2rem',
      boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
      border: '1px solid #e5e7eb'
    },
    actionsContent: {
      display: 'flex',
      flexDirection: 'column',
      gap: '1rem'
    },
    actionsRow: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      flexWrap: 'wrap',
      gap: '1rem'
    },
    searchFilters: {
      display: 'flex',
      gap: '1rem',
      alignItems: 'center',
      flexWrap: 'wrap'
    },
    searchInput: {
      padding: '0.75rem 1rem',
      paddingLeft: '2.5rem',
      border: '1px solid #d1d5db',
      borderRadius: '8px',
      fontSize: '1rem',
      width: '250px',
      position: 'relative'
    },
    searchContainer: {
      position: 'relative'
    },
    searchIcon: {
      position: 'absolute',
      left: '0.75rem',
      top: '50%',
      transform: 'translateY(-50%)',
      color: '#6b7280',
      zIndex: 1
    },
    select: {
      padding: '0.75rem 1rem',
      border: '1px solid #d1d5db',
      borderRadius: '8px',
      fontSize: '1rem',
      backgroundColor: 'white',
      cursor: 'pointer'
    },
    viewToggle: {
      display: 'flex',
      backgroundColor: '#f3f4f6',
      borderRadius: '8px',
      padding: '0.25rem'
    },
    viewButton: {
      padding: '0.5rem 0.75rem',
      border: 'none',
      borderRadius: '6px',
      cursor: 'pointer',
      transition: 'all 0.2s ease'
    },
    
    // Form Section
    formSection: {
      backgroundColor: 'white',
      padding: '2rem',
      borderRadius: '12px',
      marginBottom: '2rem',
      boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
      border: '1px solid #e5e7eb'
    },
    formTitle: {
      fontSize: '1.5rem',
      fontWeight: '600',
      color: '#1a1a1a',
      marginBottom: '1.5rem',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between'
    },
    form: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
      gap: '1.5rem'
    },
    formGroup: {
      display: 'flex',
      flexDirection: 'column'
    },
    label: {
      fontSize: '0.875rem',
      fontWeight: '500',
      color: '#374151',
      marginBottom: '0.5rem'
    },
    input: {
      width: '100%',
      padding: '0.75rem',
      border: '1px solid #d1d5db',
      borderRadius: '6px',
      fontSize: '1rem',
      transition: 'border-color 0.2s ease',
      boxSizing: 'border-box'
    },
    inputError: {
      borderColor: '#ef4444',
      backgroundColor: '#fef2f2'
    },
    textarea: {
      width: '100%',
      padding: '0.75rem',
      border: '1px solid #d1d5db',
      borderRadius: '6px',
      fontSize: '1rem',
      minHeight: '80px',
      resize: 'vertical',
      transition: 'border-color 0.2s ease',
      boxSizing: 'border-box'
    },
    errorText: {
      color: '#ef4444',
      fontSize: '0.75rem',
      marginTop: '0.25rem',
      display: 'flex',
      alignItems: 'center',
      gap: '0.25rem'
    },
    imageUploadArea: {
      border: '2px dashed #d1d5db',
      borderRadius: '8px',
      padding: '2rem',
      textAlign: 'center',
      cursor: 'pointer',
      transition: 'border-color 0.2s ease',
      backgroundColor: '#f9fafb'
    },
    imageUploadAreaActive: {
      borderColor: '#0ea5e9',
      backgroundColor: '#eff6ff'
    },
    formActions: {
      gridColumn: '1 / -1',
      display: 'flex',
      gap: '1rem',
      justifyContent: 'flex-end',
      paddingTop: '1rem',
      borderTop: '1px solid #e5e7eb'
    },
    
    // Team Grid
    teamGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
      gap: '1.5rem',
      marginTop: '1rem'
    },
    teamCard: {
      backgroundColor: '#ffffff',
      borderRadius: '12px',
      padding: '1.5rem',
      boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
      border: '1px solid #e5e7eb',
      transition: 'all 0.3s ease',
      position: 'relative'
    },
    
    // Empty state
    emptyState: {
      textAlign: 'center',
      padding: '4rem 2rem',
      color: '#6b7280'
    },
    emptyStateTitle: {
      fontSize: '1.5rem',
      marginBottom: '0.5rem',
      color: '#1a1a1a'
    },
    
    // Loading
    loadingContainer: {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      minHeight: '300px',
      color: '#6b7280'
    },
    
    // Buttons
    button: {
      padding: '0.75rem 1.5rem',
      border: 'none',
      borderRadius: '8px',
      fontSize: '1rem',
      fontWeight: '500',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem',
      transition: 'all 0.2s ease'
    },
    primaryButton: {
      backgroundColor: '#0ea5e9',
      color: 'white'
    },
    secondaryButton: {
      backgroundColor: '#6b7280',
      color: 'white'
    },
    dangerButton: {
      backgroundColor: '#ef4444',
      color: 'white'
    },
    warningButton: {
      backgroundColor: '#f59e0b',
      color: 'white'
    }
  };

  // Show loading screen on initial load
  if (initialLoading) {
    return (
      <div style={styles.pageContainer}>
        <div style={styles.loadingContainer}>
          <Loader size={24} className="animate-spin" style={{ marginRight: '0.5rem' }} />
          <span>Loading team members...</span>
        </div>
      </div>
    );
  }

  return (
    <>
      <style>{`
        .animate-spin {
          animation: spin 1s linear infinite;
        }

        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }

        @keyframes toastSlideDown {
          from { transform: translate(-50%, -120%); opacity: 0; }
          to { transform: translate(-50%, 0); opacity: 1; }
        }
        
        .team-card:hover {
          transform: translateY(-4px);
          box-shadow: 0 12px 35px rgba(0, 0, 0, 0.15);
        }
        
        .admin-button:hover {
          background-color: #0284c7;
          transform: translateY(-1px);
        }
        
        .form-input:focus, .form-textarea:focus {
          outline: none;
          border-color: #0ea5e9;
          box-shadow: 0 0 0 3px rgba(14, 165, 233, 0.1);
        }
        
        @media (max-width: 1024px) {
          .hero-title {
            font-size: 1.8rem !important;
          }
          
          .admin-controls {
            position: static !important;
            transform: none !important;
            margin-top: 1rem !important;
            justify-content: center !important;
          }
          
          .search-filters {
            flex-direction: column !important;
            align-items: stretch !important;
          }
          
          .actions-row {
            flex-direction: column !important;
            align-items: stretch !important;
          }
        }
        
        @media (max-width: 768px) {
          .team-grid {
            grid-template-columns: 1fr !important;
          }
          
          .form {
            grid-template-columns: 1fr !important;
          }
          
          .search-input {
            width: 100% !important;
          }
        }
      `}</style>

      <div style={styles.pageContainer}>
        {/* Uploading overlay — full-page block during in-flight submissions so users know to wait */}
        {submitting && (
          <div style={{
            position: 'fixed',
            inset: 0,
            backgroundColor: 'rgba(15, 23, 42, 0.45)',
            zIndex: 60,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            pointerEvents: 'auto'
          }}>
            <div style={{
              backgroundColor: 'white',
              padding: '1.5rem 2rem',
              borderRadius: '12px',
              boxShadow: '0 12px 35px rgba(0,0,0,0.2)',
              display: 'flex',
              alignItems: 'center',
              gap: '0.75rem',
              minWidth: '260px'
            }}>
              <Loader size={20} className="animate-spin" style={{ color: '#0ea5e9' }} />
              <div>
                <div style={{ fontWeight: 600, color: '#1a1a1a' }}>
                  {selectedImage ? 'Uploading image…' : 'Saving changes…'}
                </div>
                <div style={{ fontSize: '0.8125rem', color: '#6b7280' }}>
                  Please don't close this tab.
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Message Notification */}
        {message.text && (
          <div style={{
            ...styles.message,
            ...(message.type === 'success' ? styles.successMessage : styles.errorMessage)
          }}>
            {message.type === 'success' ? (
              <CheckCircle size={20} />
            ) : (
              <AlertCircle size={20} />
            )}
            <span>{message.text}</span>
            <button
              onClick={() => setMessage({ text: '', type: '' })}
              style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                marginLeft: '0.5rem'
              }}
            >
              <X size={16} />
            </button>
          </div>
        )}

        {/* Image Modal */}
        {showImageModal && modalImage && (
          <div
            role="dialog"
            aria-modal="true"
            onClick={() => setShowImageModal(false)}
            style={{
              position: 'fixed',
              inset: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.6)',
              zIndex: 50,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '1rem'
            }}>
            <div
              onClick={(e) => e.stopPropagation()}
              style={{
                backgroundColor: 'white',
                borderRadius: '12px',
                maxWidth: '600px',
                maxHeight: '90vh',
                overflow: 'hidden',
                boxShadow: '0 20px 40px rgba(0, 0, 0, 0.3)'
              }}>
              <div style={{
                padding: '1.5rem',
                borderBottom: '1px solid #e5e7eb',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}>
                <h3 style={{ fontSize: '1.25rem', fontWeight: '600', margin: 0 }}>{modalImage.name}</h3>
                <button
                  onClick={() => setShowImageModal(false)}
                  style={{
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    padding: '0.5rem'
                  }}
                >
                  <X size={24} />
                </button>
              </div>
              <div style={{ padding: '1.5rem' }}>
                <img 
                  src={modalImage.url} 
                  alt={modalImage.name}
                  style={{
                    maxWidth: '100%',
                    maxHeight: '70vh',
                    objectFit: 'contain',
                    margin: '0 auto',
                    display: 'block',
                    borderRadius: '8px'
                  }}
                />
              </div>
            </div>
          </div>
        )}
<AdminPanelHeader />
        {/* Header */}
        <section style={styles.heroSection}>
          <div style={styles.heroContainer}>
            <h1 style={styles.heroTitle} className="hero-title">
              Team Management
            </h1>
            
            {!showForm && (
              <div style={styles.adminControls} className="admin-controls">
                <button
                  onClick={() => {
                    resetForm();
                    setShowForm(true);
                  }}
                  style={styles.adminButton}
                  className="admin-button"
                >
                  <Plus size={20} />
                  Add Team Member
                </button>
              </div>
            )}
          </div>
        </section>

        {/* Main Content */}
        <main style={styles.mainSection}>
          <div style={styles.container}>
            {/* Actions Bar */}
            {!showForm && (
              <div style={styles.actionsBar}>
                <div style={styles.actionsContent}>
                  <div style={styles.actionsRow} className="actions-row">
                    {/* Search and Filters */}
                    <div style={styles.searchFilters} className="search-filters">
                      {/* Search */}
                      <div style={styles.searchContainer}>
                        <Search size={20} style={styles.searchIcon} />
                        <input
                          type="text"
                          placeholder="Search team members..."
                          value={searchTerm}
                          onChange={(e) => setSearchTerm(e.target.value)}
                          style={styles.searchInput}
                          className="search-input"
                        />
                      </div>

                      {/* Sort */}
                      <select
                        value={sortBy}
                        onChange={(e) => setSortBy(e.target.value)}
                        style={styles.select}
                      >
                        <option value="displayOrder">Sort by Order</option>
                        <option value="name">Sort by Name</option>
                        <option value="position">Sort by Position</option>
                      </select>
                    </div>

                    {/* View Mode and Stats */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                      {/* View Mode Toggle */}
                      <div style={styles.viewToggle}>
                        <button
                          onClick={() => setViewMode('grid')}
                          style={{
                            ...styles.viewButton,
                            backgroundColor: viewMode === 'grid' ? 'white' : 'transparent',
                            color: viewMode === 'grid' ? '#0ea5e9' : '#6b7280'
                          }}
                        >
                          <Grid size={16} />
                        </button>
                        <button
                          onClick={() => setViewMode('list')}
                          style={{
                            ...styles.viewButton,
                            backgroundColor: viewMode === 'list' ? 'white' : 'transparent',
                            color: viewMode === 'list' ? '#0ea5e9' : '#6b7280'
                          }}
                        >
                          <List size={16} />
                        </button>
                      </div>

                      {/* Quick Stats */}
                      <div style={{ display: 'flex', gap: '1rem', fontSize: '0.875rem' }}>
                        <span style={{ color: '#0ea5e9', fontWeight: '500' }}>
                          Total: {teamMembers.length}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Add/Edit Form */}
            {showForm && (
              <div ref={formRef} style={styles.formSection}>
                <div style={styles.formTitle}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <Users size={20} color="#0ea5e9" />
                    {editingMember ? 'Edit Team Member' : 'Add New Team Member'}
                  </div>
                  <button
                    onClick={handleCancel}
                    style={{
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      padding: '0.5rem',
                      borderRadius: '6px'
                    }}
                  >
                    <X size={20} color="#6b7280" />
                  </button>
                </div>
                
                <form onSubmit={handleSubmit} style={styles.form}>
                  {/* Name */}
                  <div style={styles.formGroup}>
                    <label style={styles.label}>Full Name *</label>
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) => handleInputChange('name', e.target.value)}
                      style={{
                        ...styles.input,
                        ...(errors.name ? styles.inputError : {})
                      }}
                      className="form-input"
                      placeholder="Enter full name"
                      required
                    />
                    {errors.name && (
                      <div style={styles.errorText}>
                        <AlertCircle size={12} />
                        {errors.name}
                      </div>
                    )}
                  </div>

                  {/* Position */}
                  <div style={styles.formGroup}>
                    <label style={styles.label}>Position *</label>
                    <input
                      type="text"
                      value={formData.position}
                      onChange={(e) => handleInputChange('position', e.target.value)}
                      style={{
                        ...styles.input,
                        ...(errors.position ? styles.inputError : {})
                      }}
                      className="form-input"
                      placeholder="e.g., Senior Developer, Manager"
                      required
                    />
                    {errors.position && (
                      <div style={styles.errorText}>
                        <AlertCircle size={12} />
                        {errors.position}
                      </div>
                    )}
                  </div>

                  {/* Display Order */}
                  <div style={styles.formGroup}>
                    <label style={styles.label}>Display Order</label>
                    <input
                      type="number"
                      value={formData.displayOrder}
                      onChange={(e) => handleInputChange('displayOrder', parseInt(e.target.value) || 1)}
                      style={styles.input}
                      className="form-input"
                      min="1"
                      placeholder="1"
                    />
                  </div>

                  {/* Description */}
                  <div style={{ ...styles.formGroup, gridColumn: '1 / -1' }}>
                    <label style={styles.label}>Description</label>
                    <textarea
                      value={formData.description}
                      onChange={(e) => handleInputChange('description', e.target.value)}
                      style={styles.textarea}
                      className="form-textarea"
                      placeholder="Brief description about the team member..."
                      rows={4}
                    />
                  </div>

                  {/* Active toggle */}
                  <div style={styles.formGroup}>
                    <label style={styles.label}>Status</label>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.75rem 0', cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={formData.isActive}
                        onChange={(e) => handleInputChange('isActive', e.target.checked)}
                        style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                      />
                      <span style={{ fontSize: '0.95rem', color: '#374151' }}>
                        {formData.isActive ? 'Active (visible on website)' : 'Inactive (hidden from website)'}
                      </span>
                    </label>
                  </div>

                  {/* Image Upload */}
                  <div style={{ ...styles.formGroup, gridColumn: '1 / -1' }}>
                    <label style={styles.label}>Profile Image</label>
                    <div
                      style={{
                        ...styles.imageUploadArea,
                        ...((selectedImage || isDragging) ? styles.imageUploadAreaActive : {})
                      }}
                      onClick={() => document.getElementById('imageInput').click()}
                      onDragOver={handleDragOver}
                      onDragEnter={handleDragOver}
                      onDragLeave={handleDragLeave}
                      onDrop={handleDrop}
                    >
                      <input
                        id="imageInput"
                        type="file"
                        accept="image/*"
                        onChange={handleImageSelect}
                        style={{ display: 'none' }}
                      />

                      {imagePreview ? (
                        <div style={{ textAlign: 'center', position: 'relative', display: 'inline-block' }}>
                          <img
                            src={imagePreview}
                            alt="Preview"
                            style={{
                              maxWidth: '200px',
                              maxHeight: '200px',
                              borderRadius: '8px',
                              marginBottom: '1rem',
                              objectFit: 'cover',
                              display: 'block'
                            }}
                          />
                          <button
                            type="button"
                            onClick={clearSelectedImage}
                            title="Remove image"
                            style={{
                              position: 'absolute',
                              top: '-8px',
                              right: '-8px',
                              backgroundColor: '#ef4444',
                              color: 'white',
                              border: '2px solid white',
                              borderRadius: '50%',
                              width: '28px',
                              height: '28px',
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              boxShadow: '0 2px 6px rgba(0,0,0,0.2)'
                            }}
                          >
                            <X size={14} />
                          </button>
                          <p style={{ margin: 0, fontSize: '0.875rem', color: '#6b7280' }}>
                            Click to change image or drop a new one
                          </p>
                        </div>
                      ) : (
                        <div style={{ textAlign: 'center', color: isDragging ? '#0ea5e9' : '#6b7280' }}>
                          <ImageIcon size={48} style={{ marginBottom: '1rem' }} />
                          <p style={{ margin: '0 0 0.5rem 0', fontWeight: '500' }}>
                            {isDragging ? 'Drop image here' : 'Click or drag an image here'}
                          </p>
                          <p style={{ margin: 0, fontSize: '0.875rem' }}>
                            JPEG, PNG, GIF (max 5MB)
                          </p>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Form Actions */}
                  <div style={styles.formActions}>
                    <button
                      type="button"
                      onClick={handleCancel}
                      style={{ ...styles.button, ...styles.secondaryButton }}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={submitting}
                      style={{
                        ...styles.button,
                        ...styles.primaryButton,
                        opacity: submitting ? 0.7 : 1,
                        cursor: submitting ? 'not-allowed' : 'pointer'
                      }}
                    >
                      {submitting ? (
                        <Loader size={16} className="animate-spin" />
                      ) : (
                        <Save size={16} />
                      )}
                      {submitting ? 'Saving...' : (editingMember ? 'Update Member' : 'Create Member')}
                    </button>
                  </div>
                </form>
              </div>
            )}

            {/* Team Members Section */}
            <div style={{
              backgroundColor: 'white',
              borderRadius: '12px',
              overflow: 'hidden',
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
              border: '1px solid #e5e7eb'
            }}>
              <div style={{
                padding: '1.5rem',
                borderBottom: '1px solid #e5e7eb',
                backgroundColor: '#f9fafb'
              }}>
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  flexWrap: 'wrap',
                  gap: '1rem'
                }}>
                  <div>
                    <h2 style={{
                      fontSize: '1.5rem',
                      fontWeight: '600',
                      color: '#1a1a1a',
                      margin: '0 0 0.25rem 0'
                    }}>
                      Team Members ({filteredAndSortedMembers.length})
                    </h2>
                    <p style={{
                      color: '#6b7280',
                      margin: 0,
                      fontSize: '0.875rem'
                    }}>
                      Manage your team members and their profile images
                    </p>
                  </div>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem'
                  }}>
                    <div style={{
                      width: '8px',
                      height: '8px',
                      backgroundColor: '#0ea5e9',
                      borderRadius: '50%'
                    }}></div>
                    <span style={{ color: '#0ea5e9', fontWeight: '500', fontSize: '0.875rem' }}>
                      Total: {teamMembers.length}
                    </span>
                  </div>
                </div>
              </div>

              {/* Team Members Grid/List */}
              {filteredAndSortedMembers.length === 0 ? (
                <div style={styles.emptyState}>
                  <div style={{
                    width: '64px',
                    height: '64px',
                    backgroundColor: '#f3f4f6',
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    margin: '0 auto 1rem'
                  }}>
                    <User size={32} color="#9ca3af" />
                  </div>
                  <h3 style={styles.emptyStateTitle}>
                    {searchTerm 
                      ? 'No matching team members found' 
                      : 'No team members yet'
                    }
                  </h3>
                  <p style={{ margin: '0.5rem 0 1.5rem', lineHeight: 1.5 }}>
                    {searchTerm 
                      ? 'Try adjusting your search criteria'
                      : 'Get started by adding your first team member to build your amazing team!'
                    }
                  </p>
                  {!searchTerm && !showForm && (
                    <button
                      onClick={() => {
                        resetForm();
                        setShowForm(true);
                      }}
                      style={{ ...styles.button, ...styles.primaryButton }}
                    >
                      <Plus size={16} />
                      Add your first team member
                    </button>
                  )}
                </div>
              ) : (
                <div style={{
                  padding: '1.5rem',
                  ...(viewMode === 'grid' ? styles.teamGrid : { display: 'flex', flexDirection: 'column', gap: '1rem' })
                }} className="team-grid">
                  {filteredAndSortedMembers.map((member) => (
                    <div
                      key={member.id}
                      style={{
                        ...styles.teamCard,
                        ...(viewMode === 'list' ? {
                          display: 'flex',
                          alignItems: 'center',
                          gap: '1.5rem',
                          padding: '1rem'
                        } : {})
                      }}
                      className="team-card"
                    >
                      {/* Member Header */}
                      <div style={{
                        display: 'flex',
                        alignItems: 'start',
                        justifyContent: 'space-between',
                        marginBottom: viewMode === 'list' ? 0 : '1rem',
                        ...(viewMode === 'list' ? { flex: '1' } : {})
                      }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                          <div style={{ position: 'relative' }}>
                            {member.imageUrl && !brokenImages.has(member.id) ? (
                              <img
                                src={getImageUrl(member)}
                                alt={member.name}
                                style={{
                                  width: viewMode === 'list' ? '60px' : '80px',
                                  height: viewMode === 'list' ? '60px' : '80px',
                                  borderRadius: '50%',
                                  objectFit: 'cover',
                                  cursor: 'pointer',
                                  border: '3px solid #f3f4f6',
                                  transition: 'all 0.2s ease'
                                }}
                                onClick={() => showImageInModal(getImageUrl(member), member.name)}
                                onError={() => {
                                  console.warn('Image failed to load:', getImageUrl(member));
                                  setBrokenImages(prev => {
                                    const next = new Set(prev);
                                    next.add(member.id);
                                    return next;
                                  });
                                }}
                              />
                            ) : member.imageUrl ? (
                              <div
                                title={`Image failed to load: ${getImageUrl(member)}`}
                                style={{
                                  width: viewMode === 'list' ? '60px' : '80px',
                                  height: viewMode === 'list' ? '60px' : '80px',
                                  borderRadius: '50%',
                                  backgroundColor: '#fef2f2',
                                  display: 'flex',
                                  flexDirection: 'column',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  border: '3px solid #fca5a5',
                                  color: '#b91c1c',
                                  fontSize: '0.65rem',
                                  fontWeight: 600
                                }}
                              >
                                <ImageOff size={viewMode === 'list' ? 18 : 24} />
                                <span style={{ marginTop: '2px' }}>broken</span>
                              </div>
                            ) : (
                              <div style={{
                                width: viewMode === 'list' ? '60px' : '80px',
                                height: viewMode === 'list' ? '60px' : '80px',
                                borderRadius: '50%',
                                backgroundColor: '#f3f4f6',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                border: '3px solid #e5e7eb'
                              }}>
                                <User size={viewMode === 'list' ? 24 : 32} color="#9ca3af" />
                              </div>
                            )}
                          </div>
                          <div>
                            <h3 style={{
                              fontSize: viewMode === 'list' ? '1.125rem' : '1.25rem',
                              fontWeight: '600',
                              color: '#1a1a1a',
                              margin: '0 0 0.25rem 0'
                            }}>
                              {member.name}
                            </h3>
                            <p style={{
                              fontSize: viewMode === 'list' ? '0.875rem' : '1rem',
                              color: '#0ea5e9',
                              fontWeight: '500',
                              margin: '0 0 0.25rem 0'
                            }}>
                              {member.position}
                            </p>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                              <span style={{
                                fontSize: '0.75rem',
                                color: '#6b7280',
                                backgroundColor: '#f3f4f6',
                                padding: '0.25rem 0.5rem',
                                borderRadius: '6px'
                              }}>
                                #{member.displayOrder || 0}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Member Details */}
                      {viewMode === 'grid' && member.description && (
                        <div style={{ marginBottom: '1rem' }}>
                          <p style={{
                            fontSize: '0.875rem',
                            color: '#6b7280',
                            margin: '0',
                            lineHeight: 1.5,
                            backgroundColor: '#f9fafb',
                            padding: '0.75rem',
                            borderRadius: '6px',
                            display: '-webkit-box',
                            WebkitLineClamp: 3,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden'
                          }}>
                            {member.description}
                          </p>
                        </div>
                      )}

                      {/* Image Upload Section */}
                      <div style={{
                        borderTop: '1px solid #f3f4f6',
                        paddingTop: '1rem',
                        marginBottom: '1rem',
                        ...(viewMode === 'list' ? { display: 'none' } : {})
                      }}>
                        <div style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          marginBottom: '0.75rem'
                        }}>
                          <label style={{
                            fontSize: '0.875rem',
                            fontWeight: '500',
                            color: '#374151',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.5rem'
                          }}>
                            <ImageIcon size={16} />
                            Profile Image
                          </label>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                          <input
                            type="file"
                            accept="image/*"
                            onChange={(e) => handleImageUpload(member.id, e)}
                            style={{ display: 'none' }}
                            id={`image-upload-${member.id}`}
                          />
                          <label
                            htmlFor={`image-upload-${member.id}`}
                            style={{
                              flex: 1,
                              backgroundColor: '#f9fafb',
                              border: '1px solid #d1d5db',
                              borderRadius: '8px',
                              padding: '0.75rem',
                              textAlign: 'center',
                              cursor: 'pointer',
                              fontSize: '0.875rem',
                              fontWeight: '500',
                              color: '#374151',
                              transition: 'all 0.2s ease',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              gap: '0.5rem'
                            }}
                          >
                            <ImageIcon size={16} />
                            {member.imageUrl ? 'Change Image' : 'Upload Image'}
                          </label>
                        </div>
                      </div>

                      {/* Actions */}
                      <div style={{
                        display: 'flex',
                        gap: '0.5rem',
                        ...(viewMode === 'list' ? { marginLeft: 'auto' } : {})
                      }}>
                        <button
                          onClick={() => handleEdit(member)}
                          style={{
                            ...styles.button,
                            padding: '0.5rem',
                            ...styles.warningButton
                          }}
                          title="Edit"
                        >
                          <Edit size={14} />
                        </button>
                        
                        <button
                          onClick={() => deleteMember(member.id)}
                          style={{
                            ...styles.button,
                            padding: '0.5rem',
                            ...styles.dangerButton
                          }}
                          title="Delete"
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Statistics Cards */}
            {teamMembers.length > 0 && !showForm && (
              <div style={{
                marginTop: '2rem',
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
                gap: '1.5rem'
              }}>
                <div style={{
                  backgroundColor: '#0ea5e9',
                  color: 'white',
                  borderRadius: '12px',
                  padding: '1.5rem',
                  boxShadow: '0 4px 20px rgba(14, 165, 233, 0.3)'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <div>
                      <div style={{ fontSize: '2rem', fontWeight: 'bold', marginBottom: '0.25rem' }}>
                        {teamMembers.length}
                      </div>
                      <div style={{ opacity: 0.9, fontSize: '0.875rem' }}>Total Members</div>
                    </div>
                    <div style={{
                      width: '48px',
                      height: '48px',
                      backgroundColor: 'rgba(255, 255, 255, 0.2)',
                      borderRadius: '12px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <User size={24} />
                    </div>
                  </div>
                </div>
                
                <div style={{
                  backgroundColor: '#8b5cf6',
                  color: 'white',
                  borderRadius: '12px',
                  padding: '1.5rem',
                  boxShadow: '0 4px 20px rgba(139, 92, 246, 0.3)'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <div>
                      <div style={{ fontSize: '2rem', fontWeight: 'bold', marginBottom: '0.25rem' }}>
                        {teamMembers.filter(m => m.imageUrl).length}
                      </div>
                      <div style={{ opacity: 0.9, fontSize: '0.875rem' }}>With Photos</div>
                    </div>
                    <div style={{
                      width: '48px',
                      height: '48px',
                      backgroundColor: 'rgba(255, 255, 255, 0.2)',
                      borderRadius: '12px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <ImageIcon size={24} />
                    </div>
                  </div>
                </div>
                
                <div style={{
                  backgroundColor: '#f59e0b',
                  color: 'white',
                  borderRadius: '12px',
                  padding: '1.5rem',
                  boxShadow: '0 4px 20px rgba(245, 158, 11, 0.3)'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <div>
                      <div style={{ fontSize: '2rem', fontWeight: 'bold', marginBottom: '0.25rem' }}>
                        {teamMembers.filter(m => !m.imageUrl).length}
                      </div>
                      <div style={{ opacity: 0.9, fontSize: '0.875rem' }}>Need Photos</div>
                    </div>
                    <div style={{
                      width: '48px',
                      height: '48px',
                      backgroundColor: 'rgba(255, 255, 255, 0.2)',
                      borderRadius: '12px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <Upload size={24} />
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </main>
      </div>
    </>
  );
};

export default TeamImageUpload;